import type {
  RegionKey,
  DashboardData,
  StripeMrrRow,
  CurrentMrrRow,
  ProductSplitRow,
} from "./sql";
import type { FxArrMap } from "./fx";
import { invoicedHistory, invoicedByTier } from "./invoiced";

export const REGIONS: RegionKey[] = ["NZ", "AU", "UK", "Other"];
export const RNAMES: Record<RegionKey, string> = {
  NZ: "New Zealand",
  AU: "Australia",
  UK: "United Kingdom",
  Other: "International",
};
export const COLORS: Record<RegionKey, string> = {
  NZ: "#2563eb",
  AU: "#059669",
  UK: "#7c3aed",
  Other: "#d97706",
};
export const CCY: Record<RegionKey, string> = {
  NZ: "NZ$",
  AU: "A$",
  UK: "\u00a3",
  Other: "US$",
};
// Dynamic month range: Sep 2025 → current month
const MO = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function buildMonths() {
  const now = new Date();
  const out: { short: string; sql: string; display: string }[] = [];
  const d = new Date(2025, 8, 1); // Sep 2025
  while (d.getFullYear() < now.getFullYear() ||
    (d.getFullYear() === now.getFullYear() && d.getMonth() <= now.getMonth())) {
    const m = MO[d.getMonth()] as string;
    const yy = String(d.getFullYear()).slice(2);
    out.push({ short: m, sql: `${m}${yy}`, display: `${m} ${d.getFullYear()}` });
    d.setMonth(d.getMonth() + 1);
  }
  return out;
}
const MONTH_RANGE = buildMonths();
export const MONTHS = MONTH_RANGE.map((m) => m.short);
export const MONTH_COUNT = MONTH_RANGE.length;
export const CURRENT_MONTH_IDX = MONTH_COUNT - 1;
const SQL_LABELS = MONTH_RANGE.map((m) => m.sql);

// Convert between month index and YYYY-MM for native <input type="month">
export function idxToYearMonth(i: number): string {
  const d = new Date(2025, 8 + i, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// YYYY-MM string for every month in our range, in order. Used to align FX rates with months.
export const MONTH_YMS: string[] = Array.from({ length: MONTH_COUNT }, (_, i) => idxToYearMonth(i));
export function yearMonthToIdx(ym: string): number {
  const parts = ym.split("-").map(Number);
  const y = parts[0] ?? 2025;
  const m = parts[1] ?? 9;
  return (y - 2025) * 12 + (m - 1) - 8; // offset from Sep 2025 (index 0)
}

const CCY_TO_REGION: Record<string, RegionKey> = {
  NZD: "NZ",
  AUD: "AU",
  GBP: "UK",
  USD: "Other",
};

// Convert a local-currency MRR amount from one region's local ccy to another's,
// routing via USD. Identity when fromRegion === toRegion. Used when a row's
// natural ccy-region (e.g. GBP→UK) differs from its assigned region (e.g. IE
// customer routed to Other) — we still want the dollar value to be right in
// the Other bucket (USD), not raw GBP.
function convertRegionCcy(
  amount: number,
  fromRegion: RegionKey,
  toRegion: RegionKey,
  fxUsd: FxArrMap,
  i: number,
): number {
  if (fromRegion === toRegion) return amount;
  const usd = amount * (fxUsd[fromRegion][i] ?? fxUsd[fromRegion][fxUsd[fromRegion].length - 1] ?? 1);
  const denom = fxUsd[toRegion][i] ?? fxUsd[toRegion][fxUsd[toRegion].length - 1] ?? 1;
  return usd / denom;
}

function sourceRegionForCcy(ccy: string): RegionKey {
  return CCY_TO_REGION[ccy] ?? "Other";
}

// Locked prior-month close MRR per region, in local currency. Used for the current
// month's 30% target so it doesn't drift when invoiced.csv values are refreshed
// (retroactive updates would otherwise re-inflate the prior month and the target).
// Update once per month after the prior month closes; leave empty to fall back to
// the live-computed prior-month MRR.
// Locked prior-month close MRR per region (consolidated). Used to compute the
// current month's 30% target so it doesn't drift as late Stripe data lands.
// Update once each month closes.
const PRIOR_MONTH_CLOSE_LOCK: Partial<Record<RegionKey, number>> = {
  // May 2026 close (locked 2026-06-01 for June target).
  // Uses the hybrid "current month" methodology (gross commitment for
  // expansion subs, latest paid cycle for stable subs) so the lock matches
  // what the Current MRR tile actually displayed on May 31 — the
  // historical-only methodology under-counts subs that expanded mid-month
  // before their next cycle invoice fired.
  // Stripe (hybrid) + invoiced.csv, local currency. Other in USD with the
  // GBP-IE portion FX-converted at May 29 close (GBP/USD 1.3427).
  NZ: 49089,  // 37,505 Stripe + 11,584 invoiced NZD
  AU: 59979,  // 57,572 Stripe + 2,407 invoiced AUD
  UK: 58772,  // 49,771 Stripe + 9,001 invoiced GBP
  Other: 1393, // 794 USD Stripe + 297 GBP Stripe × 1.3427 + 200 USD invoiced
};

// Monthly growth target multiplier per region. Default 30% MoM growth;
// override per region as needed. NZ runs at a softer 10% target reflecting a
// more mature market with less room for accelerated expansion.
const GROWTH_MULTIPLIER: Record<RegionKey, number> = {
  NZ: 1.10,
  AU: 1.30,
  UK: 1.30,
  Other: 1.30,
};

export function fmt(n: number | null | undefined): string {
  if (n == null) return "-";
  const a = Math.abs(n);
  if (a >= 1e6) return (n < 0 ? "-" : "") + (a / 1e6).toFixed(1) + "M";
  if (a >= 1e3) return (n < 0 ? "-" : "") + (a / 1e3).toFixed(1) + "k";
  return Math.round(n).toLocaleString();
}

const emptyRegionArr = (): Record<RegionKey, number[]> => ({
  NZ: Array(MONTH_COUNT).fill(0) as number[],
  AU: Array(MONTH_COUNT).fill(0) as number[],
  UK: Array(MONTH_COUNT).fill(0) as number[],
  Other: Array(MONTH_COUNT).fill(0) as number[],
});
const emptyRegionMap = (): Record<RegionKey, number> => ({ NZ: 0, AU: 0, UK: 0, Other: 0 });

// Build DashboardData from live query results (historical + current month + product split).
export function buildData(
  historicalRows: StripeMrrRow[],
  currentRows: CurrentMrrRow[],
  productSplitRows: ProductSplitRow[],
  fxUsd: FxArrMap,
  fxNzd: FxArrMap,
  priorCommitmentRows: CurrentMrrRow[],
): DashboardData {
  const CI = CURRENT_MONTH_IDX;
  const PI = CI - 1;

  // 1. Consolidated MRR = Stripe (historical + current month hybrid) + Invoiced (Xero)
  const mrr = emptyRegionArr();

  // Historical months from the historical query. Rows now come keyed by
  // (ccy, region) so e.g. IE customers paying GBP arrive as their own row with
  // region='Other'; we FX-convert the GBP amount to the Other bucket's local
  // currency (USD) before accumulating.
  for (const r of historicalRows) {
    const i = SQL_LABELS.indexOf(r.label);
    if (i < 0) continue;
    const fromRegion = sourceRegionForCcy(r.ccy);
    const converted = convertRegionCcy(r.mrr_local, fromRegion, r.region, fxUsd, i);
    mrr[r.region][i] = (mrr[r.region][i] ?? 0) + converted;
  }

  // Current month from the hybrid query
  for (const r of currentRows) {
    const fromRegion = sourceRegionForCcy(r.ccy);
    const converted = convertRegionCcy(r.mrr_local, fromRegion, r.region, fxUsd, CI);
    mrr[r.region][CI] = (mrr[r.region][CI] ?? 0) + converted;
  }

  // Prior month: override the cycle-invoice value with the current-commitment value
  // (Stripe-style attribution). Captures expansions/contractions in the month they
  // happened rather than the next billing cycle. Falls back to the historical value
  // if the override isn't available for a given currency.
  if (PI >= 0) {
    // Reset prior-month so the override replaces (not stacks on) the historical row
    for (const r of REGIONS) mrr[r][PI] = 0;
    for (const r of priorCommitmentRows) {
      const fromRegion = sourceRegionForCcy(r.ccy);
      const converted = convertRegionCcy(r.mrr_local, fromRegion, r.region, fxUsd, PI);
      mrr[r.region][PI] = (mrr[r.region][PI] ?? 0) + converted;
    }
  }

  // Add invoiced MRR on top
  const inv = invoicedHistory();
  for (const r of REGIONS) {
    for (let i = 0; i < MONTH_COUNT; i++) {
      mrr[r]![i]! += inv[r]![i] ?? 0;
    }
  }

  console.log("[MRR] Prior month Stripe-only MRR (local ccy):",
    REGIONS.map((r) => `${r}=${mrr[r][PI]! - (inv[r]![PI] ?? 0)}`).join(", "));
  console.log("[MRR] Prior month consolidated MRR (local ccy):",
    REGIONS.map((r) => `${r}=${mrr[r][PI]}`).join(", "));
  console.log("[MRR] Current month Stripe-only MRR (local ccy):",
    REGIONS.map((r) => `${r}=${mrr[r][CI]! - (inv[r]![CI] ?? 0)}`).join(", "));
  console.log("[MRR] Current month consolidated MRR (local ccy):",
    REGIONS.map((r) => `${r}=${mrr[r][CI]}`).join(", "));
  console.log("[MRR] MoM Stripe-only delta (local ccy):",
    REGIONS.map((r) => {
      const prior = mrr[r][PI]! - (inv[r]![PI] ?? 0);
      const cur = mrr[r][CI]! - (inv[r]![CI] ?? 0);
      return `${r}=${cur - prior}`;
    }).join(", "));

  // 2. Targets = prior-month close x 1.30. Current month uses the locked prior
  // close so it doesn't drift from late-arriving Stripe data; historical months
  // use their own live prior MRR.
  const tgt = emptyRegionArr();
  for (const r of REGIONS) {
    const history = mrr[r];
    const mult = GROWTH_MULTIPLIER[r];
    for (let i = 0; i < MONTH_COUNT; i++) {
      const base = (i === 0 ? history[0] : history[i - 1]) || 0;
      tgt[r][i] = Math.round(base * mult);
    }
    const lockedPriorClose = PRIOR_MONTH_CLOSE_LOCK[r];
    if (lockedPriorClose != null) tgt[r][CI] = Math.round(lockedPriorClose * mult);
  }

  // 3. Product split: Stripe + Invoiced
  const proFirms = emptyRegionMap();
  const plusFirms = emptyRegionMap();
  const proSeats = emptyRegionMap();
  const plusSeats = emptyRegionMap();
  const proMrr = emptyRegionMap();
  const plusMrr = emptyRegionMap();

  // Stripe side. Rows are keyed by (ccy, region); FX-convert mrr_local to the
  // target region's local currency before accumulating so the Other bucket
  // stays in USD even when fed by IE GBP customers.
  for (const row of productSplitRows) {
    const bucket =
      row.product === "Plus"
        ? { firms: plusFirms, seats: plusSeats, mrr: plusMrr }
        : row.product === "Pro"
          ? { firms: proFirms, seats: proSeats, mrr: proMrr }
          : null;
    if (!bucket) continue;
    const fromRegion = sourceRegionForCcy(row.ccy);
    const convertedMrr = convertRegionCcy(row.mrr_local, fromRegion, row.region, fxUsd, CI);
    bucket.firms[row.region] += row.firms;
    bucket.seats[row.region] += row.seats;
    bucket.mrr[row.region] += convertedMrr;
  }

  // Invoiced side (firms + MRR; seats not tracked)
  const invTier = invoicedByTier();
  for (const r of REGIONS) {
    proMrr[r] += invTier.mrr.Pro[r];
    plusMrr[r] += invTier.mrr.Plus[r];
    proFirms[r] += invTier.firms.Pro[r];
    plusFirms[r] += invTier.firms.Plus[r];
  }

  // 4. Headline figures
  const fxN = (r: RegionKey, i: number) => fxNzd[r][i] ?? fxNzd[r][CI] ?? 1;
  const fxU = (r: RegionKey, i: number) => fxUsd[r][i] ?? fxUsd[r][CI] ?? 1;
  const inNZD = (r: RegionKey, i: number) => (mrr[r][i] || 0) * fxN(r, i);
  const inUSD = (r: RegionKey, i: number) => (mrr[r][i] || 0) * fxU(r, i);
  const tgtNZD = (r: RegionKey) => (tgt[r][CI] || 0) * fxN(r, CI);
  const tgtUSD = (r: RegionKey) => (tgt[r][CI] || 0) * fxU(r, CI);

  const priorSumNZD = REGIONS.reduce((s, r) => s + inNZD(r, PI), 0);
  const currentSumNZD = REGIONS.reduce((s, r) => s + inNZD(r, CI), 0);
  const targetSumNZD = REGIONS.reduce((s, r) => s + tgtNZD(r), 0);
  const currentSumUSD = REGIONS.reduce((s, r) => s + inUSD(r, CI), 0);
  const targetSumUSD = REGIONS.reduce((s, r) => s + tgtUSD(r), 0);

  const today = new Date();
  const pctThroughMonth =
    today.getDate() / new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

  // 5. Per-region tracking
  const monthStart = emptyRegionMap();
  const regionCaptured = emptyRegionMap();
  for (const r of REGIONS) {
    const start = mrr[r][PI] || 0;
    const current = mrr[r][CI] || 0;
    const target = tgt[r][CI] || 0;
    monthStart[r] = start;
    regionCaptured[r] = target - start > 0 ? (current - start) / (target - start) : 0;
  }

  // 6. Historical total MRR in NZD
  const totalMRR: number[] = [];
  for (let i = 0; i < MONTH_COUNT; i++) {
    let sum = 0;
    for (const r of REGIONS) sum += (mrr[r][i] || 0) * fxN(r, i);
    totalMRR.push(Math.round(sum));
  }

  return {
    currentMRR: Math.round(currentSumNZD),
    currentARR: Math.round(currentSumNZD * 12),
    currentTargetMRR: Math.round(targetSumNZD),
    requiredForGoal: Math.round(targetSumNZD - currentSumNZD),
    currentMRR_usd: Math.round(currentSumUSD),
    currentARR_usd: Math.round(currentSumUSD * 12),
    currentTargetMRR_usd: Math.round(targetSumUSD),
    requiredForGoal_usd: Math.round(targetSumUSD - currentSumUSD),
    monthlyGrowth: priorSumNZD > 0 ? (currentSumNZD - priorSumNZD) / priorSumNZD : 0,
    pctOfTarget:
      targetSumNZD - priorSumNZD > 0
        ? (currentSumNZD - priorSumNZD) / (targetSumNZD - priorSumNZD)
        : 0,
    pctThroughMonth,
    mrr,
    tgt,
    monthStart,
    regionCaptured,
    retool: plusMrr,
    proMrr,
    totalMRR,
    fxNzd,
    fxUsd,
    pro: { companies: proFirms, seats: proSeats },
    plus: { companies: plusFirms, seats: plusSeats },
  };
}
